/**
 * Dark vector basemap using MapLibre GL layered inside Leaflet.
 *
 * Tiles: OpenFreeMap (https://openfreemap.org) — OpenMapTiles schema, no API
 * key, no signup.
 *
 * Why vector: raster OSM tiles had grey territorial-water boundary lines
 * baked into the pixels. With vector tiles we only draw LAND boundaries
 * (`maritime == 0`), so the ocean stays clean while coastlines, roads,
 * buildings and place labels remain crisp. Contact markers / clusters keep
 * using Leaflet's DOM marker pane which sits above the WebGL canvas.
 *
 * Contrast model (this is what makes the map readable — do not flatten it):
 *   land  (background)  #1f2630  charcoal
 *   water               #0b1017  clearly darker than land -> coastlines pop
 *   roads               #3d4a59 .. #93a4bb (brighter as importance rises)
 *   labels              #cfd8e3 with dark halo
 *
 * Resilience: if WebGL is unavailable, the style fails to load, or tiles keep
 * erroring, we surface a visible banner, retry with back-off, and finally
 * fall back to a raster OSM layer (CSS-darkened) so the user is never left
 * staring at a black rectangle.
 */
import { useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import maplibregl from "maplibre-gl";
import "@maplibre/maplibre-gl-leaflet";
import "maplibre-gl/dist/maplibre-gl.css";

const OPENFREEMAP_TILEJSON = "https://tiles.openfreemap.org/planet";
const FALLBACK_RASTER = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";

const LAND = "#1f2630";
const WATER = "#0b1017";
const HALO = "#141a21";

const DARK_STYLE = {
  version: 8,
  name: "crm-dark",
  glyphs: "https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf",
  sources: {
    openmaptiles: { type: "vector", url: OPENFREEMAP_TILEJSON },
  },
  layers: [
    // Land = background. OpenMapTiles has no "land" polygon layer; water is
    // painted on top, so the background colour IS the land colour.
    { id: "background", type: "background", paint: { "background-color": LAND } },
    {
      id: "landcover-green",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "landcover",
      filter: ["in", "class", "wood", "grass", "farmland"],
      paint: { "fill-color": "#1f2d29", "fill-opacity": 0.55 },
    },
    {
      id: "landcover-ice",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "landcover",
      filter: ["==", "class", "ice"],
      paint: { "fill-color": "#2a3139", "fill-opacity": 0.7 },
    },
    {
      id: "park",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "park",
      paint: { "fill-color": "#1d2e27", "fill-opacity": 0.6 },
    },
    {
      id: "landuse-residential",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "landuse",
      filter: ["in", "class", "residential", "suburb", "neighbourhood"],
      minzoom: 9,
      paint: { "fill-color": "#262e39", "fill-opacity": 0.6 },
    },
    {
      id: "landuse-industrial",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "landuse",
      filter: ["in", "class", "industrial", "commercial", "retail"],
      minzoom: 11,
      paint: { "fill-color": "#2a2c33", "fill-opacity": 0.6 },
    },
    {
      id: "water",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "water",
      paint: { "fill-color": WATER, "fill-antialias": true },
    },
    {
      id: "waterway",
      type: "line",
      source: "openmaptiles",
      "source-layer": "waterway",
      minzoom: 9,
      paint: {
        "line-color": "#132133",
        "line-width": ["interpolate", ["exponential", 1.4], ["zoom"], 9, 0.6, 16, 2.4],
      },
    },
    {
      id: "aeroway",
      type: "line",
      source: "openmaptiles",
      "source-layer": "aeroway",
      minzoom: 11,
      filter: ["in", "class", "runway", "taxiway"],
      paint: { "line-color": "#39434f", "line-width": ["interpolate", ["linear"], ["zoom"], 11, 1, 16, 8] },
    },
    {
      id: "building",
      type: "fill",
      source: "openmaptiles",
      "source-layer": "building",
      minzoom: 13,
      paint: {
        "fill-color": "#2c3540",
        "fill-outline-color": "#1a2029",
        "fill-opacity": ["interpolate", ["linear"], ["zoom"], 13, 0.4, 16, 0.9],
      },
    },
    // Land-only administrative borders. `maritime == 0` is the key filter —
    // it keeps country/state lines on land but removes the long grey lines
    // that ran through the ocean on the raster basemap.
    {
      id: "boundary-state",
      type: "line",
      source: "openmaptiles",
      "source-layer": "boundary",
      minzoom: 3,
      filter: ["all", ["==", "admin_level", 4], ["==", "maritime", 0]],
      paint: {
        "line-color": "#3a4552",
        "line-dasharray": [3, 2],
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.5, 10, 1.4],
      },
    },
    {
      id: "boundary-country",
      type: "line",
      source: "openmaptiles",
      "source-layer": "boundary",
      filter: ["all", ["==", "admin_level", 2], ["==", "maritime", 0]],
      paint: {
        "line-color": "#4a5666",
        "line-width": ["interpolate", ["linear"], ["zoom"], 2, 0.8, 10, 2],
      },
    },
    // ---- roads (dim -> bright by importance) ----
    {
      id: "road-path",
      type: "line",
      source: "openmaptiles",
      "source-layer": "transportation",
      minzoom: 14,
      filter: ["==", "class", "path"],
      paint: { "line-color": "#3a4655", "line-dasharray": [2, 2], "line-width": 1 },
    },
    {
      id: "road-minor",
      type: "line",
      source: "openmaptiles",
      "source-layer": "transportation",
      minzoom: 11,
      filter: ["in", "class", "minor", "service", "track"],
      paint: {
        "line-color": "#3d4a59",
        "line-width": ["interpolate", ["exponential", 1.3], ["zoom"], 11, 0.5, 14, 1.4, 18, 6],
      },
    },
    {
      id: "road-secondary",
      type: "line",
      source: "openmaptiles",
      "source-layer": "transportation",
      minzoom: 7,
      filter: ["in", "class", "secondary", "tertiary"],
      paint: {
        "line-color": "#556477",
        "line-width": ["interpolate", ["exponential", 1.3], ["zoom"], 7, 0.5, 12, 1.4, 18, 8],
      },
    },
    {
      id: "road-primary",
      type: "line",
      source: "openmaptiles",
      "source-layer": "transportation",
      filter: ["in", "class", "primary", "trunk"],
      paint: {
        "line-color": "#6f8197",
        "line-width": ["interpolate", ["exponential", 1.3], ["zoom"], 4, 0.4, 8, 1, 12, 2.2, 18, 11],
      },
    },
    {
      id: "road-motorway",
      type: "line",
      source: "openmaptiles",
      "source-layer": "transportation",
      filter: ["==", "class", "motorway"],
      paint: {
        "line-color": "#93a4bb",
        "line-width": ["interpolate", ["exponential", 1.3], ["zoom"], 4, 0.6, 8, 1.4, 12, 3, 18, 14],
      },
    },
    {
      id: "rail",
      type: "line",
      source: "openmaptiles",
      "source-layer": "transportation",
      minzoom: 10,
      filter: ["==", "class", "rail"],
      paint: { "line-color": "#454e5a", "line-dasharray": [4, 3], "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.6, 16, 2] },
    },
    // ---- labels ----
    {
      id: "water-name",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "water_name",
      filter: ["in", "class", "ocean", "sea"],
      layout: {
        "text-field": "{name:latin}",
        "text-font": ["Noto Sans Italic"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 2, 11, 6, 15],
        "text-letter-spacing": 0.2,
      },
      paint: { "text-color": "#46617c", "text-halo-color": WATER, "text-halo-width": 1 },
    },
    {
      id: "road-name",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "transportation_name",
      minzoom: 13,
      filter: ["in", "class", "motorway", "trunk", "primary", "secondary", "tertiary", "minor"],
      layout: {
        "symbol-placement": "line",
        "text-field": "{name:latin}",
        "text-font": ["Noto Sans Regular"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 13, 10, 17, 14],
        "text-rotation-alignment": "map",
      },
      paint: { "text-color": "#aab7c6", "text-halo-color": HALO, "text-halo-width": 1.2 },
    },
    {
      id: "place-village",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      minzoom: 10,
      filter: ["in", "class", "village", "suburb", "neighbourhood", "hamlet"],
      layout: { "text-field": "{name:latin}", "text-font": ["Noto Sans Regular"], "text-size": 11 },
      paint: { "text-color": "#9aa7b6", "text-halo-color": HALO, "text-halo-width": 1.1 },
    },
    {
      id: "place-town",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      minzoom: 6,
      filter: ["==", "class", "town"],
      layout: { "text-field": "{name:latin}", "text-font": ["Noto Sans Regular"], "text-size": ["interpolate", ["linear"], ["zoom"], 6, 10, 12, 14] },
      paint: { "text-color": "#c3cdd9", "text-halo-color": HALO, "text-halo-width": 1.2 },
    },
    {
      id: "place-city",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      filter: ["==", "class", "city"],
      layout: {
        "text-field": "{name:latin}",
        "text-font": ["Noto Sans Bold"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 4, 11, 8, 14, 12, 18],
      },
      paint: { "text-color": "#e4eaf1", "text-halo-color": HALO, "text-halo-width": 1.4 },
    },
    {
      id: "place-state",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      minzoom: 3,
      maxzoom: 8,
      filter: ["==", "class", "state"],
      layout: {
        "text-field": "{name:latin}",
        "text-font": ["Noto Sans Regular"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 3, 10, 7, 14],
        "text-transform": "uppercase",
        "text-letter-spacing": 0.12,
      },
      paint: { "text-color": "#7d8c9e", "text-halo-color": HALO, "text-halo-width": 1.2 },
    },
    {
      id: "place-country",
      type: "symbol",
      source: "openmaptiles",
      "source-layer": "place",
      maxzoom: 9,
      filter: ["==", "class", "country"],
      layout: {
        "text-field": "{name:latin}",
        "text-font": ["Noto Sans Bold"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 2, 11, 6, 19],
        "text-transform": "uppercase",
        "text-letter-spacing": 0.15,
      },
      paint: { "text-color": "#9fb0c3", "text-halo-color": HALO, "text-halo-width": 1.6 },
    },
  ],
};

const MAX_RETRIES = 3;
const STYLE_LOAD_TIMEOUT_MS = 15000;
const TILE_ERROR_THRESHOLD = 8;

function webglAvailable() {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}

export default function VectorBasemapLayer() {
  const map = useMap();
  // status: loading | ready | retrying | fallback | unsupported
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  const layerRef = useRef(null);
  const fallbackRef = useRef(null);

  useEffect(() => {
    if (!map) return;
    let disposed = false;
    let timer = null;
    let tileErrors = 0;
    let loaded = false;

    const removeVector = () => {
      if (layerRef.current) {
        try {
          map.removeLayer(layerRef.current);
        } catch {
          // already gone
        }
        layerRef.current = null;
      }
    };

    const activateFallback = () => {
      removeVector();
      if (!fallbackRef.current) {
        fallbackRef.current = L.tileLayer(FALLBACK_RASTER, {
          className: "osm-dark-invert",
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);
      }
      setStatus(webglAvailable() ? "fallback" : "unsupported");
    };

    const retry = () => {
      if (disposed) return;
      clearTimeout(timer);
      if (attempt >= MAX_RETRIES) {
        activateFallback();
        return;
      }
      setStatus("retrying");
      removeVector();
      // Back-off: 1s, 2s, 4s
      setTimeout(() => !disposed && setAttempt((a) => a + 1), 1000 * 2 ** attempt);
    };

    if (!webglAvailable()) {
      activateFallback();
      return undefined;
    }

    if (fallbackRef.current) {
      map.removeLayer(fallbackRef.current);
      fallbackRef.current = null;
    }

    const layer = L.maplibreGL({ style: DARK_STYLE, attributionControl: false });
    layer.addTo(map);
    layerRef.current = layer;
    setStatus("loading");

    map.attributionControl?.addAttribution(
      '&copy; <a href="https://openfreemap.org">OpenFreeMap</a> · &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    );

    const gl = layer.getMaplibreMap();
    const onLoad = () => {
      loaded = true;
      clearTimeout(timer);
      setStatus("ready");
    };
    const onError = (e) => {
      // Style/TileJSON failure is fatal; a burst of tile errors before the
      // first successful paint also means the basemap is not usable.
      const msg = String(e?.error?.message || "");
      const fatal = !e?.tile && (/style|json|source/i.test(msg) || e?.sourceId === "openmaptiles");
      if (fatal && !loaded) {
        retry();
        return;
      }
      if (e?.tile) {
        tileErrors += 1;
        if (!loaded && tileErrors >= TILE_ERROR_THRESHOLD) retry();
      }
    };
    const onContextLost = () => retry();

    gl.once("load", onLoad);
    gl.on("error", onError);
    gl.getCanvas().addEventListener("webglcontextlost", onContextLost);
    timer = setTimeout(() => {
      if (!loaded) retry();
    }, STYLE_LOAD_TIMEOUT_MS);

    return () => {
      disposed = true;
      clearTimeout(timer);
      try {
        gl.off("error", onError);
        gl.getCanvas()?.removeEventListener("webglcontextlost", onContextLost);
      } catch {
        // map torn down
      }
      removeVector();
    };
  }, [map, attempt]);

  // Cleanup the raster fallback on unmount
  useEffect(
    () => () => {
      if (fallbackRef.current) {
        try {
          map.removeLayer(fallbackRef.current);
        } catch {
          // ignore
        }
        fallbackRef.current = null;
      }
    },
    [map]
  );

  if (status === "ready" || status === "loading") return null;

  const messages = {
    retrying: `Basemap failed to load — retrying (${Math.min(attempt + 1, MAX_RETRIES)}/${MAX_RETRIES})…`,
    fallback: "Vector basemap unavailable — showing fallback map.",
    unsupported: "WebGL is unavailable on this device — showing fallback map.",
  };

  return (
    <div className="leaflet-bottom leaflet-left" style={{ pointerEvents: "none", marginBottom: 28 }}>
      <div
        className="leaflet-control basemap-banner"
        role="status"
        data-testid="basemap-error-banner"
        style={{ pointerEvents: "auto" }}
      >
        <span data-testid="basemap-error-message">{messages[status]}</span>
        {status !== "unsupported" && status !== "retrying" && (
          <button
            type="button"
            data-testid="basemap-retry-btn"
            onClick={() => {
              if (fallbackRef.current) {
                map.removeLayer(fallbackRef.current);
                fallbackRef.current = null;
              }
              setAttempt(0);
              setStatus("loading");
            }}
          >
            Retry vector map
          </button>
        )}
      </div>
    </div>
  );
}

void maplibregl;
